import { useState, useEffect } from "react"
import { Button } from "src/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { useRoles } from "../hooks/useMembers"
import { createInvitation } from "../api/invitationsApi"

export function InviteMemberModal({ open, onClose, onSuccess }) {
  const { toast } = useToast()
  const { roles, loading: rolesLoading, fetch: fetchRoles } = useRoles()

  const [email, setEmail] = useState("")
  const [roleId, setRoleId] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (open && roles.length === 0) {
      fetchRoles()
    }
  }, [open, roles.length, fetchRoles])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setEmail("")
        setRoleId("")
        setErrors({})
      }, 200)
    }
  }, [open])

  // The backend already filters to EDITABLE_ROLE_SLUGS via RolePolicy::Scope
  // so we can use the roles directly
  const editableRoles = roles

  function validate() {
    const newErrors = {}
    
    if (!email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!roleId) {
      newErrors.role = "Please select a role"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!validate()) return

    setSubmitting(true)
    setErrors({})

    try {
      await createInvitation({ email: email.trim(), role_id: parseInt(roleId) })
      toast({
        title: "Invitation sent",
        description: `An invitation email has been sent to ${email}`,
      })
      onSuccess?.()
      onClose()
    } catch (error) {
      const serverErrors = error.response?.data?.errors || ["Failed to send invitation"]
      toast({
        variant: "destructive",
        title: "Error",
        description: serverErrors[0],
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new member to your organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="member@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (errors.email) setErrors((prev) => ({ ...prev, email: null }))
              }}
              disabled={submitting}
              className={errors.email ? "border-red-500" : ""}
              autoFocus
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Role Field */}
          <div className="space-y-2">
            <Label htmlFor="role">
              Role
            </Label>
            <Select
              value={roleId}
              onValueChange={(value) => {
                setRoleId(value)
                if (errors.role) setErrors((prev) => ({ ...prev, role: null }))
              }}
              disabled={submitting || rolesLoading}
            >
              <SelectTrigger 
                id="role" 
                className={errors.role ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {editableRoles.map((role) => (
                  <SelectItem key={role.id} value={role.id.toString()}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-sm text-red-500">{errors.role}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || rolesLoading}
            >
              {submitting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
